import React, { FormEvent, useEffect, useState, useRef } from 'react';
import { OfferModel } from '../models/OfferModel';
import { set } from 'date-fns';
import offerService from '../services/offerService';
import ConfirmDialogueComp from './ConfirmDialogueComp';

interface EditOfferCompProps {
    show: boolean;
    offer: OfferModel;
    onClose: () => void;
    onOfferUpdated: (updatedOffer: OfferModel) => void;
}

interface OfferFormErrors {
    qty?: string;
    discount?: string;
    notes?: string;
    state?: string;
}

const EditOfferComp: React.FC<EditOfferCompProps> = ({ show, offer, onClose, onOfferUpdated }) => {

    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);

    const [confirmStatusChangeModalVisible, setconfirmStatusChangeModalVisible] = useState(false);
    const [offerQty, setOfferQty] = useState(offer.quantity);
    const [offerDiscount, setOfferDiscount] = useState(offer.discount);

    const [offerStatus, setOfferStatus] = useState(offer.state);
    const [pendingStatus, setPendingStatus] = useState<string | null>(null);
    const [statusList] = useState(['Processing', 'Completed', 'Canceled']);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [offerFormErrors, setOfferFormErrors] = useState<OfferFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener('hidden.bs.modal', onClose);
        }
        return () => {
            modalRef.current?.removeEventListener('hidden.bs.modal', onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            setOfferQty(offer.quantity);
            setOfferDiscount(offer.discount);
            setOfferStatus(offer.state);
            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    const validateOffer = () => {
        const errors: OfferFormErrors = {};
        if (!offerQty || offerQty <= 0) {
            errors.qty = "Quantity is required and must be greater than 0";
        }

        if (offerDiscount && (isNaN(offerDiscount) || offerDiscount < 0)) {
            errors.discount = "Discount must be a valid number greater than or equal to 0";
        }

        if (!offerStatus) {
            errors.state = "Offer status is required";
        }

        setOfferFormErrors(errors);
        return Object.keys(errors).length === 0;
    }

    const handleChangeOfferStatus = (newStatus: string) => {
        if (newStatus === "Completed" || newStatus === "Canceled") {
            setPendingStatus(newStatus);
            setconfirmStatusChangeModalVisible(true);
        }
        else {
            setOfferStatus(newStatus);
        }

    }

    const handleConfirmStatusChange = () => {
        if (pendingStatus) {
            setOfferStatus(pendingStatus);  // apply the new status
        }
        setPendingStatus(null);
        setconfirmStatusChangeModalVisible(false);
    }

    const handleCancelStatusChange = () => {
        setPendingStatus(null);
        setOfferStatus(offer.state);
        setconfirmStatusChangeModalVisible(false);
    }

    const handleOfferSubmit = (e: FormEvent) => {
        const updateOffer = async () => {
            try {
                const updateSucceed = await offerService.updateOffer(offer.offerId, offerQty, offerDiscount, offerStatus);
                if (updateSucceed === "Success") {
                    const updatedOffer: OfferModel = {
                        ...offer,
                        quantity: offerQty,
                        discount: offerDiscount,
                        state: offerStatus
                    };
                    onOfferUpdated(updatedOffer);
                } else {
                    setServiceStatus({ type: "Error", message: "Failed to update the offer." });
                }
            }
            catch (err) {
                setServiceStatus({ type: "Error", message: "Failed to update the offer." });
            }
            setIsSubmitting(false);
        };
        e.preventDefault();
        setServiceStatus({ type: "", message: "" });
        if (validateOffer()) {
            setIsSubmitting(true);
            updateOffer();
        }
    };

    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content rounded-0">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Offer</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => bsModalRef.current?.hide()}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className='row g-3'>
                            <div className="col-md-12">
                                <label htmlFor="inputOfferQty" className="form-label">Qty</label>
                                <input
                                    type="number"
                                    className={`form-control ${offerFormErrors.qty ? 'is-invalid' : ''}`}
                                    min={1}
                                    step="1"
                                    value={offerQty}
                                    onChange={(e) => setOfferQty(Number(e.target.value))}
                                    id="inputOfferQty"
                                />
                                {offerFormErrors.qty && <div className="invalid-feedback">{offerFormErrors.qty}</div>}
                            </div>
                            <div className="col-md-12">
                                <label htmlFor="inputOfferDiscount" className="form-label">Discount</label>
                                <input
                                    type="number"
                                    className={`form-control ${offerFormErrors.discount ? 'is-invalid' : ''}`}
                                    min={0}
                                    step="0.01"
                                    value={offerDiscount}
                                    onChange={(e) => setOfferDiscount(Number(e.target.value))}
                                    id="inputOfferDiscount"
                                />
                                {offerFormErrors.discount && <div className="invalid-feedback">{offerFormErrors.discount}</div>}
                            </div>
                            <div className="col-md-12">
                                <label htmlFor="inputStatus" className="form-label">Status</label>
                                <select
                                    id="inputStatus"
                                    className={`form-select ${offerFormErrors.state ? ` is-invalid` : ``}`}
                                    value={offerStatus}
                                    onChange={(e) => handleChangeOfferStatus(e.target.value)}>
                                    <option disabled value="">Choose...</option>
                                    <option value="Created" disabled>
                                        Created
                                    </option>
                                    {statusList.map((status, index) => (
                                        <option key={index} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </select>
                                {offerFormErrors.state && <div className="invalid-feedback">{offerFormErrors.state}</div>}
                            </div>
                            {serviceStatus.type === "Error" && <div className="col-md-12"><div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div></div></div>}
                        </div>
                    </div>
                    <div className="modal-footer d-flex justify-content-between">
                        <button className="btn btn-outline-dark rounded-0" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-dark rounded-0" onClick={handleOfferSubmit} disabled={isSubmitting}>
                            {isSubmitting ? (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...</>) : ("Save")}
                        </button>
                    </div>
                </div>
            </div >
            {confirmStatusChangeModalVisible && (
                <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center">
                    <div className="bg-white p-4 rounded shadow-lg" style={{ maxWidth: "500px"}}>
                        <h5>Confirm</h5>
                        <p style={{ whiteSpace: "pre-line" }}>
                            Once an offer is marked as <strong>Canceled</strong> or <strong>Completed</strong>, the status cannot be changed.
                            {"\n"}<strong>Are you sure you want to continue?</strong>
                        </p>


                        <div className="d-flex justify-content-between">
                            <button className="btn btn-outline-dark" onClick={handleCancelStatusChange}>
                                No
                            </button>
                            <button className="btn btn-danger" onClick={handleConfirmStatusChange}>
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div >

    );

};

export default EditOfferComp;